import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl, Marker, Tooltip, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./HydrologicalData.css";
import { MapHomeControl, MapInfoControl } from "../../../../common/MapControls/MapControls";

const INITIAL_VIEW_STATE = {
    longitude: 125.7275,
    latitude: -8.8742,
    zoom: 8,
};

const HydrologicalData = () => {
    const [boundaryData, setBoundaryData] = useState(null);
    const [wlData, setWlData] = useState(null);
    const [wtpData, setWtpData] = useState(null);
    const [tankData, setTankData] = useState(null);
    const [boreholeData, setBoreholeData] = useState(null);
    const [beeMatanData, setBeeMatanData] = useState(null);

    const [selectedData, setSelectedData] = useState(null);
    const [selectedCoords, setSelectedCoords] = useState(null);

    // Toggles
    const [showWL, setShowWL] = useState(true);
    const [showWtp, setShowWtp] = useState(true);
    const [showTank, setShowTank] = useState(true);
    const [showBorehole, setShowBorehole] = useState(true);
    const [showBeeMatan, setShowBeeMatan] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [mapInstance, setMapInstance] = useState(null);

    useEffect(() => {
        fetch("/DATA/OSMbased_adm1_boundary.json")
            .then((res) => res.json())
            .then((data) => setBoundaryData(data));

        fetch("/DATA/Cyclone/HydrologicalData/WL_TIMOR.json")
            .then((res) => res.json())
            .then((data) => setWlData(data));

        fetch("/DATA/Cyclone/HydrologicalData/Water_treatment_plants.json")
            .then((res) => res.json())
            .then((data) => setWtpData(data));

        fetch("/DATA/Cyclone/HydrologicalData/Water_tank.json")
            .then((res) => res.json())
            .then((data) => setTankData(data));

        fetch("/DATA/Cyclone/HydrologicalData/Borehole_TL.json")
            .then((res) => res.json())
            .then((data) => setBoreholeData(data));

        fetch("/DATA/Cyclone/HydrologicalData/Bee_matan_TL.json")
            .then((res) => res.json())
            .then((data) => setBeeMatanData(data));
    }, []);

    // Extractors
    const extractWL = (p) => ({ Name: p.Name, Lat: p.Lat, Long: p.Long });
    const extractWTP = (p) => ({ Name: p.Name });
    const extractTank = (p) => ({ TYPE: p.TYPE, DISTRICT: p.DISTRICT, SUCO: p.SUCO });
    const extractBorehole = (p) => ({ Name: p.Name });
    const extractBeeMatan = (p) => ({ Name: p.Name });

    // Data to memoized lists
    const waterLevels = useMemo(() => {
        if (!wlData?.features) return [];
        return wlData.features.map((f) => {
            const coords = [f.geometry.coordinates[1], f.geometry.coordinates[0]];
            return { type: "wl", coords, props: extractWL(f.properties) };
        });
    }, [wlData]);

    const wtps = useMemo(() => {
        if (!wtpData?.features) return [];
        return wtpData.features.map((f) => {
            const coords = [f.geometry.coordinates[1], f.geometry.coordinates[0]];
            return { type: "wtp", coords, props: extractWTP(f.properties) };
        });
    }, [wtpData]);

    const tanks = useMemo(() => {
        if (!tankData?.features) return [];
        return tankData.features.map((f) => {
            const coords = [f.geometry.coordinates[1], f.geometry.coordinates[0]];
            return { type: "tank", coords, props: extractTank(f.properties) };
        });
    }, [tankData]);

    const boreholes = useMemo(() => {
        if (!boreholeData?.features) return [];
        return boreholeData.features.map((f) => {
            const coords = [f.geometry.coordinates[1], f.geometry.coordinates[0]];
            return { type: "borehole", coords, props: extractBorehole(f.properties) };
        });
    }, [boreholeData]);

    const beeMatans = useMemo(() => {
        if (!beeMatanData?.features) return [];
        return beeMatanData.features.map((f) => {
            const coords = [f.geometry.coordinates[1], f.geometry.coordinates[0]];
            return { type: "beematan", coords, props: extractBeeMatan(f.properties) };
        });
    }, [beeMatanData]);

    // Filters
    const q = searchTerm.trim().toLowerCase();
    const filterBySearch = (items, key = "Name") =>
        items.filter((i) => (i.props[key] || "").toLowerCase().includes(q));

    const fWL = q ? filterBySearch(waterLevels) : waterLevels;
    const fWTP = q ? filterBySearch(wtps) : wtps;
    const fTank = q ? filterBySearch(tanks, "TYPE") : tanks;
    const fBore = q ? filterBySearch(boreholes) : boreholes;
    const fBee = q ? filterBySearch(beeMatans) : beeMatans;

    const totalVisible =
        (showWL ? fWL.length : 0) +
        (showWtp ? fWTP.length : 0) +
        (showTank ? fTank.length : 0) +
        (showBorehole ? fBore.length : 0) +
        (showBeeMatan ? fBee.length : 0);

    // Marker icons
    const wlIcon = new L.Icon({ iconUrl: "/DATA/Cyclone/HydrologicalData/WaterLevel.png", iconSize: [25, 25] });
    const wtpIcon = new L.Icon({ iconUrl: "/DATA/Cyclone/HydrologicalData/WaterTreatmentPlants.png", iconSize: [25, 25] });
    const tankIcon = new L.Icon({ iconUrl: "/DATA/Cyclone/HydrologicalData/water_tank.png", iconSize: [25, 25] });
    const boreIcon = new L.Icon({ iconUrl: "/DATA/Cyclone/HydrologicalData/borehole.png", iconSize: [25, 25] });
    const beeIcon = new L.Icon({ iconUrl: "/DATA/Cyclone/HydrologicalData/bee_matan.png", iconSize: [25, 25] });

    const handleSelect = (item) => {
        setSelectedData(item.props);
        setSelectedCoords(item.coords);
        if (mapInstance) mapInstance.flyTo(item.coords, Math.max(mapInstance.getZoom(), 13), { duration: 1 });
    };

    return (
        <div className="hc-wrapper">
            <div className="hc-panel open">
                <div className="hc-panel-header">
                    <div className="hc-title">Hydrological Data</div>
                    <span className="hc-count-badge">{totalVisible}</span>
                </div>
                <div className="hc-panel-content">
                    <div className="hc-controls">
                        <div className="hc-toggles-flood">
                            <label><input type="checkbox" checked={showWL} onChange={e => setShowWL(e.target.checked)} /> <img className="hc-type-icon" src="/DATA/Cyclone/HydrologicalData/WaterLevel.png" alt="" /> Water Levels</label>
                            <label><input type="checkbox" checked={showWtp} onChange={e => setShowWtp(e.target.checked)} /> <img className="hc-type-icon" src="/DATA/Cyclone/HydrologicalData/WaterTreatmentPlants.png" alt="" /> Water Treatment</label>
                            <label><input type="checkbox" checked={showTank} onChange={e => setShowTank(e.target.checked)} /> <img className="hc-type-icon" src="/DATA/Cyclone/HydrologicalData/water_tank.png" alt="" /> Water Tanks</label>
                            <label><input type="checkbox" checked={showBorehole} onChange={e => setShowBorehole(e.target.checked)} /> <img className="hc-type-icon" src="/DATA/Cyclone/HydrologicalData/borehole.png" alt="" /> Boreholes</label>
                            <label><input type="checkbox" checked={showBeeMatan} onChange={e => setShowBeeMatan(e.target.checked)} /> <img className="hc-type-icon" src="/DATA/Cyclone/HydrologicalData/bee_matan.png" alt="" /> Bee Matan</label>
                        </div>
                        <input className="hc-search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name..." />
                    </div>
                    <div className="hc-list">
                        {showWL && fWL.map((i, idx) => <button key={`wl-${idx}`} className="hc-list-item" onClick={() => handleSelect(i)}>{i.props.Name || "Water Level"}</button>)}
                        {showWtp && fWTP.map((i, idx) => <button key={`wtp-${idx}`} className="hc-list-item" onClick={() => handleSelect(i)}>{i.props.Name || "WTP"}</button>)}
                        {showTank && fTank.map((i, idx) => <button key={`tank-${idx}`} className="hc-list-item" onClick={() => handleSelect(i)}>{i.props.TYPE || "Tank"}</button>)}
                        {showBorehole && fBore.map((i, idx) => <button key={`bore-${idx}`} className="hc-list-item" onClick={() => handleSelect(i)}>{i.props.Name || "Borehole"}</button>)}
                        {showBeeMatan && fBee.map((i, idx) => <button key={`bee-${idx}`} className="hc-list-item" onClick={() => handleSelect(i)}>{i.props.Name || "Bee Matan"}</button>)}
                        {totalVisible === 0 && <div className="hc-empty">No facilities match your filters.</div>}
                    </div>
                </div>
            </div>

            <MapContainer center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]} attributionControl={false} zoom={INITIAL_VIEW_STATE.zoom} className="mapcontainerall" whenCreated={setMapInstance}>
              <MapHomeControl />
              <MapInfoControl text="Hydrological stations and river network data used for flood monitoring." />
                <LayersControl position="topright">
                    <LayersControl.BaseLayer name="White Theme" checked>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="OSM">
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    </LayersControl.BaseLayer>
                </LayersControl>

                {boundaryData && <GeoJSON data={boundaryData} style={{ color: "#000", weight: 2, opacity: 1, fillOpacity: 0 }} />}

                {showWL && fWL.map((i, idx) => <Marker key={`mwl-${idx}`} position={i.coords} icon={wlIcon} eventHandlers={{ click: () => handleSelect(i) }}><Tooltip>{i.props.Name}</Tooltip></Marker>)}
                {showWtp && fWTP.map((i, idx) => <Marker key={`mwtp-${idx}`} position={i.coords} icon={wtpIcon} eventHandlers={{ click: () => handleSelect(i) }}><Tooltip>{i.props.Name}</Tooltip></Marker>)}
                {showTank && fTank.map((i, idx) => <Marker key={`mtank-${idx}`} position={i.coords} icon={tankIcon} eventHandlers={{ click: () => handleSelect(i) }}><Tooltip>{i.props.TYPE}</Tooltip></Marker>)}
                {showBorehole && fBore.map((i, idx) => <Marker key={`mbore-${idx}`} position={i.coords} icon={boreIcon} eventHandlers={{ click: () => handleSelect(i) }}><Tooltip>{i.props.Name}</Tooltip></Marker>)}
                {showBeeMatan && fBee.map((i, idx) => <Marker key={`mbee-${idx}`} position={i.coords} icon={beeIcon} eventHandlers={{ click: () => handleSelect(i) }}><Tooltip>{i.props.Name}</Tooltip></Marker>)}

                {selectedCoords && <CircleMarker center={selectedCoords} radius={14} pathOptions={{ color: "#1e88e5", weight: 2, fillOpacity: 0.15 }} />}
            </MapContainer>

            {selectedData && (
                <div className="hc-details">
                    <div className="hc-details-header">
                        <div className="hc-details-title">Details</div>
                        <button className="hc-close" onClick={() => { setSelectedData(null); setSelectedCoords(null); }}>✕</button>
                    </div>
                    <div className="hc-details-body">
                        {Object.entries(selectedData).map(([k, v]) => (
                            <div className="hc-detail-row" key={k}>
                                <div className="hc-detail-key">{k}</div>
                                <div className="hc-detail-value">{v || "N/A"}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HydrologicalData;
